import { embedContext } from './osEntry';
import { useEffect, useState } from 'react';

export type Theme = 'light' | 'dark';

const STORAGE_KEY = 'ss_theme';
// Public pages temporarily change the DOM, not the admin's resolved choice.
// Keep this session value independent, including embedded choices that must
// never overwrite the standalone preference in localStorage.
let adminTheme: Theme | undefined;

function readStored(): Theme | null {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    if (v === 'light' || v === 'dark') return v;
  } catch {
    // ignore
  }
  return null;
}

function apply(theme: Theme) {
  document.documentElement.dataset.theme = theme;
}

/**
 * Light-first. The warm-paper brand is the default everywhere; dark is an
 * admin-only opt-in, persisted only when an admin explicitly toggles it.
 * We deliberately do NOT follow the OS `prefers-color-scheme` — the brand is
 * light by design, not by system preference.
 */
export function initTheme() {
  adminTheme = embedContext.theme ?? readStored() ?? 'light';
  apply(adminTheme);
}

export function useTheme(): { theme: Theme; toggle: () => void; set: (t: Theme) => void } {
  const [theme, setTheme] = useState<Theme>(() => {
    if (typeof document === 'undefined') return 'light';
    return adminTheme ?? embedContext.theme ?? readStored() ?? 'light';
  });

  useEffect(() => {
    adminTheme = theme;
    apply(theme);
    try {
      if (!embedContext.embedded) localStorage.setItem(STORAGE_KEY, theme);
    } catch {
      // ignore
    }
  }, [theme]);

  return {
    theme,
    toggle: () => setTheme((t) => (t === 'dark' ? 'light' : 'dark')),
    set: setTheme,
  };
}

/**
 * Force the warm-paper light theme for this view and never expose dark.
 * Used by public / client / crew pages so the brand-facing surfaces always
 * read as paper, regardless of any dark choice the admin made elsewhere.
 * Does not touch the stored preference.
 */
export function useForceLight() {
  useEffect(() => {
    const prev = document.documentElement.dataset.theme;
    document.documentElement.dataset.theme = 'light';
    return () => {
      // Restore the stored/admin theme when leaving the public view.
      document.documentElement.dataset.theme = adminTheme ?? prev ?? readStored() ?? 'light';
    };
  }, []);
}
